import React from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import AppShell from './components/AppShell';

// Import all enhanced components
import Login from './pages/Login';
import DashboardEnhanced from './pages/DashboardEnhanced';
import PropertiesEnhanced from './pages/PropertiesEnhanced';
import PropertyManagement from './pages/PropertyManagement';
import Renters from './pages/Renters';
import ContractsEnhanced from './pages/ContractsEnhanced';
import TicketsUnified from './pages/TicketsUnified';
import Finance from './pages/Finance';
import Violations from './pages/Violations';
import CVMonitoring from './pages/CVMonitoring';
import CVSetup from './pages/CVSetup';
import Settings from './pages/Settings';
import UserManagement from './components/admin/UserManagement';
import PaymentManagement from './components/admin/PaymentManagement';
import MeterReadingsManagement from './components/admin/MeterReadingsManagement';
import MyContract from './pages/MyContract';
import RenterPayments from './pages/RenterPayments';
import MeterUpload from './pages/MeterUpload';
import VehicleManagement from './pages/VehicleManagement';
import CalendarPage from './pages/CalendarPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0, // Always refetch to ensure data is fresh
    },
  },
});

function AppRoutes() {
  const { t } = useTranslation('common');
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="flex flex-col items-center gap-3 text-stone-500">
          <svg className="animate-spin w-8 h-8 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden>
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
          </svg>
          <p className="text-sm font-medium">{t('status.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Unified login at root - Login component handles all role-based redirects */}
      <Route path="/" element={<Login />} />
      <Route path="/login" element={<Login />} />

      {/* Manager Portal Routes */}
      <Route
        path="/manager"
        element={
          <ProtectedRoute requiredRole={['manager', 'admin']}>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/manager/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardEnhanced />} />
        <Route path="properties" element={<PropertiesEnhanced />} />
        <Route path="property-management" element={<PropertyManagement />} />
        <Route path="renters" element={<Renters />} />
        <Route path="users" element={<UserManagement />} />
        <Route path="contracts" element={<ContractsEnhanced />} />
        <Route path="tickets" element={<TicketsUnified />} />
        <Route path="tickets-enhanced" element={<TicketsUnified />} />
        <Route path="finance" element={<Finance />} />
        <Route path="payment-management" element={<PaymentManagement />} />
        <Route path="violations" element={<Violations />} />
        <Route path="meter-readings" element={<MeterReadingsManagement />} />
        <Route path="cv-monitoring" element={<CVMonitoring />} />
        <Route path="cv-setup" element={<CVSetup />} />
        <Route path="vehicles" element={<VehicleManagement />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="settings" element={<Settings />} />
        <Route path="my-contract" element={<MyContract />} />
        <Route path="my-payments" element={<RenterPayments />} />
        <Route path="my-meters" element={<MeterUpload />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthProvider>
          <NotificationProvider>
            <AppRoutes />
          </NotificationProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
